import { PublicIdSchemas } from '@idc-forms/id'
import { z } from 'zod'
import { EconomicDeclarationShape } from '../entities/client/economic'
import { EquityDeclarationShape } from '../entities/client/equity'
import { IndividualApplicantShape } from '../entities/client/individual'
import { LegalRepresentativeSchema } from '../entities/client/legal-representatives'
import { PepDeclarationShape } from '../entities/client/pep'
import { CompanyApplicantShape } from '../portal/juridical/company'
import { InstantSchema } from '../primitives/time'
import { storedShape } from '../utils/stored-shape'
import { V2CursorPaginationSchema } from './http'

/**
 * Contrato de los EXPEDIENTES DEL PORTAL en API v2 — la lectura admin (carril D).
 *
 * ── ☠️ LA REGLA QUE MANDA EN TODO EL ARCHIVO ─────────────────────────────────
 *
 * **La credencial no cruza el wire.** Ni una vez, ni en el detalle, ni «sólo para el que puede
 * reenviar el enlace». El `accessToken` abre el KYC de un cliente —nombre, DNI, ingresos,
 * documentos— sin autenticarse, y todo lo que entra a una respuesta termina en el cache del
 * navegador, en un `console.log` de alguien depurando y en el cuerpo que un proxy registra.
 *
 * v1 sí lo publica, y no por descuido: su pantalla arma el enlace en el front. En v2 eso se
 * resuelve al revés —el servidor manda el correo, el front nunca ve la llave— y por eso
 * `v2-admin-portal-requests.service.spec.ts` tiene un caso que serializa la respuesta entera y
 * busca el token adentro. Es el mismo caso que ya existe en el listado de clientes.
 *
 * ── EL SUJETO ────────────────────────────────────────────────────────────────
 *
 * Una fila es un EXPEDIENTE: el pedido de validación que se le mandó a un cliente. No es el
 * cliente ni el formulario — un mismo cliente puede tener varios expedientes a lo largo del
 * tiempo, y como máximo uno vivo.
 *
 * ── QUÉ CAMBIA CONTRA v1 ─────────────────────────────────────────────────────
 *
 * 1. **Se direcciona por `pfr_`, no por el uuid crudo.** v1 usa `GET requests/:id` con el `id`
 *    uuid. Ese uuid sobrevive en la base porque catorce rutas v1 lo usan; `pfr_` es lo único que
 *    cruza el wire v2 (ADR 0008). El campo es nuevo: hasta el 2026-08-12 el `pfr_` se lo llevaba
 *    la credencial, que es justo lo que la decisión Nº 44 vino a corregir.
 *
 * 2. **El listado pagina.** v1 responde `findByClient(clientId)` — el array entero, sin cursor,
 *    sin orden declarado y **obligando a filtrar por cliente**: no existe forma de ver los
 *    expedientes vencidos de toda la cartera, ni los que esperan revisión. Acá el cliente es un
 *    filtro más (ADR 0019/0020).
 *
 * 3. **Las fechas son instantes RFC 3339.** v1 devuelve lo que Mongoose serialice.
 *
 * 4. **El progreso viaja calculado.** v1 obliga a cada consumidor a contar secciones enviadas
 *    contra secciones habilitadas — y la web y el móvil lo contaban distinto.
 */

/**
 * Los doce estados de un expediente.
 *
 * Se publican los doce y no un resumen de tres: la diferencia entre `sent` y `opened` es si el
 * cliente vio el correo, y entre `submitted` y `resubmitted` es si ya hubo una vuelta de
 * correcciones. Colapsarlos le sacaría a la pantalla información que hoy muestra.
 */
export const V2PortalRequestStatus = {
  DRAFT: 'draft',
  SENT: 'sent',
  OPENED: 'opened',
  IN_PROGRESS: 'in_progress',
  SUBMITTED: 'submitted',
  UNDER_REVIEW: 'under_review',
  CHANGES_REQUESTED: 'changes_requested',
  RESUBMITTED: 'resubmitted',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  EXPIRED: 'expired',
  REVOKED: 'revoked'
} as const
export type V2PortalRequestStatus =
  (typeof V2PortalRequestStatus)[keyof typeof V2PortalRequestStatus]

export const V2PortalRequestStatusSchema = z.enum([
  V2PortalRequestStatus.DRAFT,
  V2PortalRequestStatus.SENT,
  V2PortalRequestStatus.OPENED,
  V2PortalRequestStatus.IN_PROGRESS,
  V2PortalRequestStatus.SUBMITTED,
  V2PortalRequestStatus.UNDER_REVIEW,
  V2PortalRequestStatus.CHANGES_REQUESTED,
  V2PortalRequestStatus.RESUBMITTED,
  V2PortalRequestStatus.APPROVED,
  V2PortalRequestStatus.REJECTED,
  V2PortalRequestStatus.EXPIRED,
  V2PortalRequestStatus.REVOKED
])

/**
 * Qué se le está validando al cliente.
 *
 * ☠️ **Son CINCO y el contrato declaraba DOS** (`registration | revalidation`), valores que además
 * no existen en el dominio. Los reales son los de `PortalValidationType` en la API, y `expedient`
 * es el que tiene el 100% de los expedientes de dev.
 *
 * El defecto no daba error del lado del servidor porque el presentador **castea** en vez de mapear
 * (`row.validationType as …`), así que el valor salía tal cual con un tipo que mentía. Lo atajó
 * `assertShape` en el navegador — para lo que está— pero la pantalla se comía el rechazo y mostraba
 * su estado vacío: el listado del portal se veía «sin flujos» con cinco flujos en la base.
 */
export const V2PortalValidationTypeSchema = z.enum([
  'new_client',
  'expedient',
  'revalidation',
  'profile',
  'equity'
])

/** Persona individual o jurídica: define qué secciones tiene el formulario. */
export const V2PortalClientTypeSchema = z.enum(['individual', 'juridical'])

/**
 * El cliente al que se le pidió la validación.
 *
 * Viaja como referencia con id público y no poblado: quien necesite el expediente del cliente lo
 * pide a su propio recurso. Se incluye el nombre porque **toda** pantalla que lista expedientes lo
 * muestra, y sin él cada fila obligaría a un viaje más.
 */
export const V2PortalRequestClientSchema = z.object({
  id: PublicIdSchemas.client,
  name: z.string(),
  /**
   * El correo al que se mandó el enlace.
   *
   * Es un atributo del EXPEDIENTE y no del cliente: se sella al crearlo, así que si el cliente
   * cambia de correo después, este sigue diciendo a dónde se mandó de verdad.
   */
  email: z.string()
})

/**
 * Cuánto del formulario completó el cliente.
 *
 * ☠️ **Derivado en el servidor a propósito.** v1 devuelve `enabledSections` y `submissions` y deja
 * que cada consumidor haga la cuenta — y la web y el móvil la hacían distinto porque el criterio de
 * «sección completa» no estaba escrito en ningún lado. Un número que dos clientes calculan distinto
 * no es un dato: es dos datos con el mismo nombre.
 */
export const V2PortalRequestProgressSchema = z.object({
  /** Cuántas secciones habilitó el flujo para este expediente. */
  total: z.number().int().nonnegative(),
  /** Cuántas de ésas el cliente ya envió. */
  submitted: z.number().int().nonnegative()
})

/**
 * Una fila del listado.
 *
 * **No lleva el contenido del formulario.** Los datos personales, la declaración PEP y el estado
 * patrimonial son KYC y sólo se sirven en el detalle: una pantalla que lista cincuenta expedientes
 * no necesita el DNI de cincuenta personas, y mandarlo los pone en un cache que nadie pidió.
 */
export const V2AdminPortalRequestSchema = z.object({
  id: PublicIdSchemas.portalFormRequest,
  client: V2PortalRequestClientSchema,
  clientType: V2PortalClientTypeSchema,
  validationType: V2PortalValidationTypeSchema,
  status: V2PortalRequestStatusSchema,
  progress: V2PortalRequestProgressSchema,

  /**
   * Cuándo vence el enlace.
   *
   * `null` cuando el expediente todavía es borrador: el reloj arranca al enviarlo, no al crearlo.
   */
  expiresAt: InstantSchema.nullable(),
  /** Cuándo salió el correo con el enlace. `null` si todavía no salió. */
  sentAt: InstantSchema.nullable(),
  /**
   * Cuándo el cliente abrió el correo, según el pixel de seguimiento.
   *
   * ⚠️ Es un dato **optimista**: un cliente que bloquea imágenes lo deja en `null` habiendo abierto.
   * Sirve para «probablemente no lo vio», nunca para «no lo vio».
   */
  openedAt: InstantSchema.nullable(),
  /** Cuándo el cliente envió el formulario para revisión. `null` mientras no lo haya enviado. */
  submittedAt: InstantSchema.nullable(),

  /**
   * Las secciones que el flujo le habilitó a este expediente.
   *
   * ☠️ Estaba **proyectada y no publicada**: el pipeline ya la leía para calcular `progress` y la
   * descartaba. La pantalla de revisión la necesita entera —no el conteo— para saber qué pasos
   * dibujar, así que devolver sólo `progress` la dejaba armando el wizard a ciegas.
   */
  enabledSections: z.array(z.string()),

  /**
   * ☠️ **Los recordatorios automáticos que se le mandaron al cliente** (política: cada 2 días,
   * máximo 3).
   *
   * Faltaban en el contrato y **su ausencia no daba error**: las cinco pantallas que los leen
   * escriben `?? 0`, así que migrarlas sin esto habría mostrado «0 recordatorios» para siempre —
   * incluida una columna entera de la tabla de flujos y una tarjeta del resumen. El asesor habría
   * leído que a un cliente no se lo persiguió cuando se lo persiguió tres veces.
   *
   * Un dato ausente se nota; un cero falso se cree.
   */
  reminderCount: z.number().int().nonnegative(),
  /** Cuándo salió el último recordatorio. `null` si todavía no salió ninguno. */
  lastReminderAt: InstantSchema.nullable(),

  createdAt: InstantSchema,
  updatedAt: InstantSchema
})
export type V2AdminPortalRequest = z.infer<typeof V2AdminPortalRequestSchema>

export const V2AdminPortalRequestsListSchema = z.object({
  data: z.array(V2AdminPortalRequestSchema),
  meta: z.object({ pagination: V2CursorPaginationSchema })
})
export type V2AdminPortalRequestsList = z.infer<typeof V2AdminPortalRequestsListSchema>

/**
 * Los cuerpos de las siete transiciones.
 *
 * ── LA ASIMETRÍA ENTRE RECHAZAR Y REVOCAR ES DEL NEGOCIO ────────────────────
 *
 * El motivo del **rechazo es obligatorio** y el de la **revocación no**, y no es un descuido: el
 * rechazo se le notifica al cliente —tiene que poder saber por qué le rebotaron el expediente— y la
 * revocación no se le notifica, así que su motivo es auditoría interna.
 */
export const V2RejectPortalRequestSchema = z.object({
  /** Se le muestra al cliente. Por eso no puede venir vacío. */
  reason: z.string().trim().min(1)
})

export const V2RevokePortalRequestSchema = z.object({
  /** Auditoría interna: el cliente no lo ve. */
  reason: z.string().trim().min(1).optional()
})

export const V2RequestPortalChangesSchema = z.object({
  /**
   * Comentario global, opcional: las correcciones concretas salen de los `fieldReviews` ya
   * marcados. Un comentario que repita lo que ya dice cada campo es ruido.
   */
  comment: z.string().trim().min(1).optional()
})

export const V2ExtendPortalLinkSchema = z.object({
  /**
   * Días a partir de hoy. Omitirlo reusa el plazo con el que nació el expediente, que es lo que
   * hace v1 — y lo que quiere quien sólo pretende «dale otra semana como la primera».
   */
  expirationDays: z.number().int().positive().max(365).optional()
})

/**
 * ── LA REVISIÓN POR CAMPO ────────────────────────────────────────────────────
 *
 * El revisor no aprueba o rechaza el expediente entero de una: marca **campo por campo**. Eso es lo
 * que hace posible «corregí sólo el NIT» en vez de «rehacé todo», y es la diferencia entre una
 * vuelta de correcciones y tres.
 */

/** Las ocho secciones del formulario KYC. */
export const V2PortalSectionSchema = z.enum([
  'company',
  'applicant',
  'contact',
  'pep',
  'economic',
  'equity',
  'legal_representatives',
  'attachments'
])

/**
 * El veredicto sobre un campo.
 *
 * `resubmitted` **no lo escribe el revisor**: lo pone el sistema cuando el cliente vuelve a mandar
 * un campo que estaba en corrección. Se publica en el enum porque el revisor lo LEE —es lo que
 * distingue «pendiente de mirar de nuevo» de «pendiente de siempre»— y se rechaza en la escritura,
 * donde no significa nada que alguien lo declare.
 */
export const V2PortalFieldReviewStatusSchema = z.enum([
  'approved',
  'correction',
  'not_applicable',
  'resubmitted'
])
export const V2PortalWritableFieldReviewStatusSchema = z.enum([
  'approved',
  'correction',
  'not_applicable'
])

/**
 * Sobre qué recae la marca.
 *
 * No todo lo corregible es un campo suelto: una fila de un arreglo —un PEP de más— o una sección
 * entera se marcan como unidad. Sin `scope`, corregir «sobra un PEP» obligaría a inventar un campo
 * que no existe en el formulario.
 */
export const V2PortalFieldReviewScopeSchema = z.enum(['field', 'group', 'section', 'array', 'form'])

export const V2PortalFieldReviewSchema = z.object({
  /** La ruta del campo dentro del formulario, tal como la nombra el front. */
  field: z.string().trim().min(1),
  section: V2PortalSectionSchema,
  scope: V2PortalFieldReviewScopeSchema.optional(),
  status: V2PortalWritableFieldReviewStatusSchema,
  /**
   * Lo que el cliente va a leer.
   *
   * Obligatorio cuando el estado es `correction` y prohibido cuando no: una corrección sin motivo
   * le deja al cliente un campo en rojo y ninguna forma de saber qué arreglar, que es la queja que
   * origina la segunda vuelta.
   */
  comment: z.string().trim().min(1).nullable().optional()
})

export const V2SavePortalFieldReviewsSchema = z.object({
  reviews: z.array(V2PortalFieldReviewSchema).min(1)
})

export const V2DeletePortalFieldReviewSchema = z.object({
  field: z.string().trim().min(1),
  section: V2PortalSectionSchema
})

/**
 * ── LOS DOCUMENTOS DEL EXPEDIENTE ────────────────────────────────────────────
 *
 * El KYC pide papeles, y el expediente los organiza en **slots**: «DPI anverso», «RTU», «poder del
 * representante». Un slot no es una carpeta — es un requisito con reglas propias (a quién aplica,
 * qué formatos acepta, si es obligatorio) y **como máximo un documento adentro**.
 *
 * ☠️ Por eso esto NO se resolvió con el endpoint genérico `POST /admin/documents`, que existe y
 * acepta `ownerType: 'portal-form-request'`. Ese sube contra una CARPETA; acá se sube contra un
 * REQUISITO, y el requisito es el que decide si el archivo es válido y si el expediente está
 * completo. Reusar el genérico habría dejado el documento colgado del expediente sin slot, o sea
 * invisible para la compuerta que verifica que el KYC esté completo.
 */

/**
 * Un slot: qué papel se pide y bajo qué reglas.
 *
 * `visibility` es el corte que separa lo que el cliente ve de lo que no. Un requisito `internal`
 * **el cliente nunca lo ve**: lo aporta el asesor —una constancia que recibió en persona, un
 * informe interno— y aparecer en el formulario público sería pedirle al cliente un papel que no
 * puede tener.
 */
export const V2PortalDocumentRequirementSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  fileTypesAllowed: z.array(z.string()),
  maxFileSizeMb: z.number().int().positive(),
  /** Obligatorio para cerrar el expediente. */
  isRequired: z.boolean(),
  /** Obligatorio para que el CLIENTE pueda enviar el formulario. Es un corte más estrecho. */
  clientRequired: z.boolean(),
  visibility: z.enum(['public', 'internal'])
})

/**
 * Un documento ya subido.
 *
 * ☠️ **`url` es una URL FIRMADA de vida corta, y por eso no es un campo estable.** El `path` del
 * objeto en el almacenamiento no se publica nunca: exponerlo dejaría el bucket y la key a la vista
 * de cualquiera que lea una respuesta, y esas keys no rotan.
 */
export const V2PortalDocumentSchema = z.object({
  id: z.string(),
  /** El slot al que pertenece. */
  requirementId: z.string(),
  name: z.string(),
  size: z.number().int().nonnegative(),
  contentType: z.string(),
  url: z.string()
})

export const V2AdminPortalDocumentsSchema = z.object({
  /**
   * Los slots aplicables a ESTE expediente, ya filtrados por tipo de persona y tipo de validación.
   * La superficie admin los pide TODOS, incluidos los `internal` — que es lo que la pública no hace.
   */
  requirements: z.array(V2PortalDocumentRequirementSchema),
  documents: z.array(V2PortalDocumentSchema)
})
export type V2AdminPortalDocuments = z.infer<typeof V2AdminPortalDocumentsSchema>
export type V2PortalDocument = z.infer<typeof V2PortalDocumentSchema>

/**
 * El slot de documento. Su `id` es el uuid de `clientconfigs.attachmentRequirements[].id` — legacy
 * y no migrable sin tocar el dato, porque cada documento subido lo referencia.
 */
export type V2PortalDocumentRequirement = z.infer<typeof V2PortalDocumentRequirementSchema>

/**
 * ── EL ALTA DEL EXPEDIENTE ───────────────────────────────────────────────────
 *
 * Crear un expediente **manda el correo en el mismo acto**. v1 lo hace en dos pasos —crear y
 * después `sendLink`— y el contrato v2 no reproduce esa costura: un expediente creado y no enviado
 * es un borrador que nadie mira, y la única razón de que existiera el paso intermedio es que v1 no
 * tenía dónde ponerlo.
 */
export const V2CreatePortalRequestSchema = z.object({
  client: PublicIdSchemas.client,
  /**
   * El flujo que dirige el expediente: qué secciones se habilitan y con qué nivel de validación.
   *
   * Omitirlo obliga a declarar `validationType` a mano, que es el camino legacy. Se acepta porque
   * hay flujos viejos sin definición, no porque sea equivalente.
   */
  flowDefinition: z.string().trim().min(1).optional(),
  validationType: V2PortalValidationTypeSchema.optional(),
  /**
   * A qué correo se manda el enlace.
   *
   * Es un atributo del EXPEDIENTE, no del cliente: se sella al crearlo. Si el cliente cambia de
   * correo después, este sigue diciendo a dónde se mandó de verdad — que es lo que uno quiere
   * saber cuando alguien dice «no me llegó».
   */
  clientEmail: z.string().trim().email().optional(),
  expirationDays: z.number().int().positive().max(365).optional()
})

/**
 * ── EL ALTA RÁPIDA ───────────────────────────────────────────────────────────
 *
 * Crea el cliente **y** le manda el flujo, en un acto. Por eso exige las DOS capacidades
 * (`CreateClient` y `CreatePortalRequest`): quien sólo puede una de las dos no puede hacer esto por
 * la puerta de atrás.
 */
export const V2QuickCreatePortalClientSchema = z.object({
  clientType: V2PortalClientTypeSchema,
  firstName: z.string().trim().min(1).optional(),
  lastName: z.string().trim().min(1).optional(),
  companyName: z.string().trim().min(1).optional(),
  identificationNumber: z.string().trim().min(1).optional(),
  identificationType: z.string().trim().min(1).optional(),
  clientEmail: z.string().trim().email(),
  /** El `usr_` del asesor. Omitirlo deja el cliente sin asesor asignado. */
  advisor: PublicIdSchemas.user.optional(),
  flowDefinition: z.string().trim().min(1).optional(),
  validationType: V2PortalValidationTypeSchema.optional()
})

/** La verificación de identidad previa al alta rápida: se consulta el registro antes de crear. */
export const V2VerifyQuickClientSchema = z.object({
  clientType: V2PortalClientTypeSchema,
  identificationNumber: z.string().trim().min(1),
  identificationType: z.string().trim().min(1).optional()
})

/**
 * ── LAS MÉTRICAS DE REVALIDACIÓN ─────────────────────────────────────────────
 *
 * El tablero de quién debe revalidar. Se publica como objeto plano y no como un `data/meta` porque
 * **no es una colección**: es una foto de un momento, y envolverla en la forma de un listado
 * invitaría a paginarla.
 */
export const V2RevalidationMetricsSchema = z.object({
  /** Cada cuántos meses se revalida, según el flujo activo. */
  frequencyMonths: z.number().int().positive(),
  clients: z.object({
    /** Los que tienen alguna verificación KYC sellada. Es el denominador de todo lo demás. */
    verified: z.number().int().nonnegative(),
    /** Verificados dentro de la ventana: revalidación vigente. */
    valid: z.number().int().nonnegative(),
    /** Verificados fuera de la ventana: revalidación vencida. */
    expired: z.number().int().nonnegative(),
    /** `valid / verified`. Se publica calculado porque dividir por cero es la mitad del bug. */
    upToDatePct: z.number()
  }),
  /**
   * Los ACCIONABLES: vencidos, con contrato SAP vigente y sin revalidación en curso.
   *
   * Es el número que importa y por eso viaja aparte de `clients`: `expired` incluye a los que ya
   * tienen una revalidación andando, y perseguir a ésos es trabajo repetido.
   */
  contractValidNotRevalidated: z.number().int().nonnegative(),
  /** Clientes cuyo contrato ya se firmó en SAP: su ciclo de revalidación está cerrado. */
  closedCount: z.number().int().nonnegative(),
  completion: z.object({
    /** Revalidaciones aprobadas con datos de tiempo: envío del correo → aprobación. */
    completedCount: z.number().int().nonnegative(),
    /** Promedio de días de esa vuelta. `null` mientras no haya ninguna completa. */
    avgDays: z.number().nullable()
  }),
  /**
   * Cuántas revalidaciones confirmaron sin tocar nada.
   *
   * Es la métrica que dice si el ciclo vale la pena: si el 95% confirma sin cambios, revalidar cada
   * seis meses le está pidiendo a la gente que confirme lo que ya confirmó.
   */
  changes: z.object({
    withSummary: z.number().int().nonnegative(),
    confirmedUnchanged: z.number().int().nonnegative(),
    modified: z.number().int().nonnegative(),
    /** `null` mientras no haya ninguna con resumen: un porcentaje sobre cero no es cero. */
    confirmedUnchangedPct: z.number().nullable(),
    /** Las secciones que más se actualizan, de mayor a menor. Dice dónde envejecen los datos. */
    sectionsMostUpdated: z.array(
      z.object({ section: z.string(), count: z.number().int().nonnegative() })
    )
  })
})
export type V2RevalidationMetrics = z.infer<typeof V2RevalidationMetricsSchema>

/**
 * Lo que el registro devuelve al verificar una identidad antes del alta rápida.
 *
 * ☠️ **`applicant` viaja como objeto abierto y eso es deliberado.** Su forma la decide RENAP, no
 * este contrato: declararla campo por campo la congelaría contra un proveedor externo que puede
 * agregar o renombrar los suyos sin avisarnos, y un contrato que miente sobre un tercero es peor
 * que uno que dice «esto viene de afuera».
 *
 * Lo que sí es contrato es **cuándo NO hay respuesta**: un DPI mal formado es `400`, un DPI que el
 * registro no conoce es `404`, y un registro caído es `503`. Las tres son distinguibles, que es lo
 * que la pantalla necesita para decir «revisá los dígitos» en vez de «intentá más tarde».
 */
export const V2VerifiedQuickClientSchema = z.object({
  applicant: z.record(z.unknown()),
  /** Foto del registro, en base64. `null` si el registro no la tiene. */
  photo: z.string().nullable()
})

/** El sello de la verificación manual: cuándo se reinició el reloj del ciclo. */
export const V2MarkVerifiedResultSchema = z.object({
  verifiedAt: InstantSchema
})

/**
 * ☠️ **La ÚNICA respuesta de toda la superficie admin que lleva la credencial adentro.**
 *
 * Llega por `POST :id/link` y `POST :id/link/rotate`, y por ningún otro lado: el listado, el
 * detalle, el dossier y las siete transiciones siguen sin devolverla. Que sea excepcional es la
 * propiedad — un valor que abre el KYC de un cliente sin autenticar tiene que salir por una puerta
 * que se pueda señalar, no por el mismo camino que los demás campos.
 *
 * ── LO QUE ESTO IMPLICA PARA QUIEN LA CONSUMA ───────────────────────────────
 *
 * **No se guarda.** Ni en un cache de queries, ni en estado de componente que sobreviva a la
 * pantalla, ni en `localStorage`. Se pide, se usa —al portapapeles— y se suelta. Del lado de la
 * web eso significa `useMutation` y nunca `useQuery`: el resultado de una query ES un cache.
 *
 * **No se dibuja.** Un `<input value={url}>` la deja en capturas de pantalla, en dumps del DOM y
 * en el session replay de Sentry. El asesor no necesita leerla: necesita pegarla.
 */
export const V2PortalLinkSchema = z.object({
  /** La URL completa del formulario, lista para compartir. */
  url: z.string(),
  /**
   * Cuándo deja de abrir. `null` mientras el expediente no tenga vencimiento sellado.
   *
   * Viaja para que la pantalla pueda decir «vence en 3 días» ANTES de que el asesor lo pegue en un
   * chat, en vez de que se entere el cliente al abrirlo.
   */
  expiresAt: InstantSchema.nullable()
})

export type V2PortalLink = z.infer<typeof V2PortalLinkSchema>

/**
 * ── EL DOSSIER COMPLETO ──────────────────────────────────────────────────────
 *
 * Lo que la pantalla de revisión necesita para funcionar, y lo que `GET :id` **no daba**: el
 * listado y el detalle publicaban la ficha del expediente —estado, progreso, fechas— y no su
 * CONTENIDO. Medido antes de agregarlo: la web lee `verifiedFields`, `documentRequirements`,
 * `previous` y `sectionReview` en **52 lugares**.
 *
 * ── ☠️ LOS SANDBOXES SE REUSAN, NO SE REDECLARAN ────────────────────────────
 *
 * `applicant`, `company`, `pep`, `economic`, `equity` y `legalRepresentatives` son los schemas KYC
 * de `@idc-forms/schemas`, **los mismos que valida el formulario del cliente**. Se componen desde
 * ahí en vez de describirse otra vez acá.
 *
 * Copiarlos habría creado una segunda fuente de verdad sobre la forma del KYC — y la que se olvida
 * de actualizar es siempre la del contrato, así que el día que alguien agregue un campo al
 * formulario, la API lo rechazaría o lo recortaría sin que nadie lo note. Es exactamente el defecto
 * que el ADR 0004 vino a matar cuando unificó los formularios.
 */

/** Una marca de revisión, tal como el revisor la dejó, con su rastro. */
export const V2PortalFieldReviewReadSchema = z.object({
  field: z.string(),
  section: V2PortalSectionSchema,
  scope: V2PortalFieldReviewScopeSchema.nullable(),
  status: V2PortalFieldReviewStatusSchema,
  comment: z.string().nullable(),
  reviewedAt: InstantSchema.nullable(),

  /**
   * ☠️ **Quién dejó la marca — y no es un dato decorativo: es lo que hace aplicable un permiso.**
   *
   * `DeleteOthersCorrections` distingue borrar una corrección PROPIA (siempre permitido) de borrar
   * la de otro revisor. Sin este campo la pantalla no puede saber cuál es cuál, así que tiene que
   * elegir entre ocultarle el botón a todo el mundo o mostrárselo a todos y que el servidor rechace.
   * Las dos opciones son peores que mandar el id.
   */
  reviewerId: z.string().nullable(),

  /**
   * Los estados anteriores de esta marca.
   *
   * Es el rastro de la revisión: quién puso qué y cuándo, antes de lo que se ve ahora. La pantalla
   * lo muestra para que una corrección discutida se pueda reconstruir sin ir a la bitácora.
   */
  history: z
    .array(
      z.object({
        status: V2PortalFieldReviewStatusSchema,
        comment: z.string().nullable(),
        reviewerId: z.string().nullable(),
        reviewedAt: InstantSchema.nullable()
      })
    )
    .default([])
})

/**
 * El estado derivado de una sección, para el stepper.
 *
 * ☠️ Viaja **calculado** por el mismo motivo que `progress`: v1 devuelve las marcas crudas y deja
 * que cada consumidor las resuma, y dos consumidores que resumen distinto son dos verdades con el
 * mismo nombre. `recheck` es el que nadie deriva igual: significa «el cliente ya recorrigió y falta
 * mirarlo de nuevo», y confundirlo con `pending` esconde trabajo listo para revisar.
 */
export const V2PortalSectionReviewSchema = z.object({
  section: V2PortalSectionSchema,
  total: z.number().int().nonnegative(),
  approved: z.number().int().nonnegative(),
  corrections: z.number().int().nonnegative(),
  notApplicable: z.number().int().nonnegative(),
  resubmitted: z.number().int().nonnegative(),
  status: z.enum(['pending', 'verified', 'corrections', 'recheck'])
})

/** La sección tal como se PUBLICA: el contrato del OpenAPI y el SDK. */
const publishedSection = <T extends z.ZodRawShape>(shape: z.ZodObject<T>) => shape.partial()

/** Cómo se declara una sección: la forma que se PUBLICA o la que se EXIGE al validar. */
type SectionMode = typeof publishedSection

/**
 * La sección tal como el REGISTRO LA GUARDÓ — la que se EXIGE al validar una respuesta.
 *
 * ☠️ `.partial()` SÓLO ABRE EL PRIMER NIVEL, y ahí estaba el defecto: hacía opcional la clave
 * `residence`, pero si el objeto existía se validaba ENTERO, y adentro `country` y `address` son
 * `requiredString()` — reglas del SUBMIT de un formulario, heredadas por una LECTURA. Un cliente a
 * mitad de llenado, con la residencia empezada y sin dirección todavía, bastaba: la API devolvía
 * un 200 con el expediente entero y el portero de la web lo descartaba. El asesor no podía revisar
 * un expediente que existía y estaba bien. No es sólo `residence`: le pasa igual a
 * `identificationCard`, `birthplace`, `name` y a cada representante legal.
 */
const storedSection = <T extends z.ZodRawShape>(shape: z.ZodObject<T>) =>
  storedShape(shape.partial())

const dossierSchema = (section: SectionMode) =>
  V2AdminPortalRequestSchema.extend({
    /** Qué secciones habilitó el flujo. El formulario del cliente muestra exactamente éstas. */
    enabledSections: z.array(V2PortalSectionSchema),

    /**
     * Los datos que el cliente cargó, por sección.
     *
     * `null` en la sección que su tipo de persona no tiene: una individual no llena `company` y una
     * jurídica no llena `applicant`. Es `null` y no un objeto vacío porque «no aplica» y «vacío» son
     * cosas distintas — la segunda invita a mostrar un formulario en blanco que nadie debería llenar.
     */
    applicant: section(IndividualApplicantShape).nullable(),
    company: section(CompanyApplicantShape).nullable(),
    pep: section(PepDeclarationShape).nullable(),
    economic: section(EconomicDeclarationShape).nullable(),
    equity: section(EquityDeclarationShape).nullable(),

    /**
     * Las rutas que el registro civil ya verificó.
     *
     * Son **fuente de verdad y quedan bloqueadas para el revisor también**: si RENAP dice que el
     * nombre es ése, corregirlo a mano sería contradecir al registro desde una pantalla.
     */
    /**
     * Los representantes legales de una persona jurídica.
     *
     * ☠️ **Faltaba, y el comentario de este mismo bloque decía que estaba** — lo enumeraba entre los
     * schemas KYC que el dossier lleva, tres líneas más arriba del objeto que no lo incluía. La
     * pantalla de revisión lo lee, así que sin esto la sección se dibujaba vacía sobre datos que el
     * cliente sí había llenado.
     *
     * Va con las entradas en `.partial()` y no con `LegalRepresentativeDeclaration`: esa es la
     * versión ESTRICTA del submit —con refinamientos entre campos— y el dossier lee trabajo EN CURSO,
     * que por definición todavía no la cumple. Es el mismo criterio que las otras cinco secciones.
     */
    legalRepresentatives: z
      .object({ representatives: z.array(section(LegalRepresentativeSchema)).default([]) })
      .nullable(),

    /**
     * Cuán exigente es el formulario para este expediente.
     *
     * Decide qué campos son obligatorios y cuánto se le pide al cliente antes de dejarlo enviar, así
     * que una pantalla que no lo tiene no puede reproducir las reglas del servidor — y tampoco
     * estaba en el contrato.
     */
    validationLevel: z.enum(['draft', 'partial', 'complete']),

    verifiedFields: z.array(z.string()),

    fieldReviews: z.array(V2PortalFieldReviewReadSchema),
    sectionReview: z.array(V2PortalSectionReviewSchema),

    requirements: z.array(V2PortalDocumentRequirementSchema),
    documents: z.array(V2PortalDocumentSchema),

    /**
     * La foto de la última verificación aprobada, para el diff anterior↔nuevo.
     *
     * `null` salvo en una REVALIDACIÓN: en un alta no hay «anterior», y devolver un objeto vacío
     * haría que la pantalla dibujara un diff contra la nada.
     */
    previous: z.record(z.unknown()).nullable()
  })

export const V2AdminPortalDossierSchema = dossierSchema(publishedSection)

/**
 * El expediente TAL COMO EL REGISTRO LO GUARDÓ. Es el que hay que usar para VALIDAR una respuesta
 * — la web lo aplica en `assert-shape.ts`.
 *
 * ── POR QUÉ SON DOS Y NO UNO ──────────────────────────────────────────────────
 *
 * Mismo motivo, mismo remedio y mismo texto que `V2AdminClientStoredSchema` en `admin-clients.ts`,
 * una pantalla más allá: el contrato publicado SOBRE-PROMETE y arreglarlo es un trabajo aparte.
 *
 * ── LA DEUDA, ESCRITA PARA QUE NO SE PIERDA ───────────────────────────────────
 *
 * Corregir el spec para que diga la verdad son **742 cambios que `oasdiff` clasifica como
 * incompatibles** en `GET /admin/portal/requests/{id}`: 306 «became nullable», 295 «became
 * optional», 129 `minLength`, 9 `minItems` y 3 `format: email`. TODOS de respuesta, ninguno de
 * request. No son incompatibles en el sentido que importa —la API ya se comportaba así; el
 * contrato es el que mentía— pero `oasdiff` no puede distinguir «se aflojó» de «se corrigió», y
 * ese guard existe para proteger a los consumidores del SDK. El archivo de excepciones admite una
 * sola causa —un contrato que NUNCA se publicó— y ésta no es: el endpoint vive en `main` desde el
 * #328. Enumerar 742 líneas contra las 6 que hay no sería una excepción, sería apagar el check.
 *
 * MIENTRAS ESTO SEAN DOS, el spec le miente a quien lo lea: promete campos que pueden no venir. Un
 * consumidor nuevo puede confiar en esa promesa y romperse en runtime, y el guard de CI no lo va a
 * avisar, porque el contrato publicado no cambió. El riesgo no desapareció: cambió de lugar y
 * quedó anotado.
 *
 * CERRARLO = publicar este schema COMO el contrato, absorber los 742 y avisar a los consumidores.
 * Es su propio PR, con versionado y nota de migración — y conviene hacerlo junto con los 4516 de
 * `admin-clients`, que es la misma deuda en la pantalla de al lado. Este `export` desaparece el
 * día que los dos vuelvan a ser uno.
 */
export const V2AdminPortalDossierStoredSchema = dossierSchema(storedSection)

export type V2AdminPortalDossier = z.infer<typeof V2AdminPortalDossierSchema>
